import type { ReactNode } from 'react';
import type { BlockField } from '@/blocks/domain/types';

const MEDIA_COPY: Partial<Record<BlockField['style'], string>> = {
  image: 'Image goes here',
  logo: 'Logo goes here'
};

/** An empty text element, so a fresh Custom block is not a blank sheet the author cannot see or select. */
const TEXT_COPY: Partial<Record<BlockField['style'], string>> = {
  title: 'Add heading',
  heading: 'Add heading',
  subheading: 'Add subheading',
  rich_text: 'Add text'
};

/**
 * Editor-canvas stand-in for an element that has nothing to draw yet, so the author can see and select it:
 * a dashed box for an image / logo, a muted line of hint text for an empty heading / subheading / Text. Mounted
 * only on the block editor canvas (a canvas structure is present) - the read-only preview, the quote canvas and
 * the PDF keep printing nothing for an empty element.
 */
export function renderEmptyMediaPlaceholder(field: BlockField): ReactNode {
  const mediaCopy = MEDIA_COPY[field.style];
  if (mediaCopy) {
    return (
      <div
        data-testid={`document-render:field:${field.id}:empty-media`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: field.style === 'logo' ? 48 : 72,
          border: 'var(--border-width-xs) dashed var(--border-neutral)',
          borderRadius: 'var(--border-radius-none)',
          color: 'var(--text-caption)',
          fontSize: 12
        }}
      >
        {mediaCopy}
      </div>
    );
  }
  const textCopy = TEXT_COPY[field.style];
  if (!textCopy) return null;
  return (
    <div
      data-testid={`document-render:field:${field.id}:empty-text`}
      style={{ color: 'var(--text-placeholder)', fontStyle: 'italic', minHeight: 24 }}
    >
      {textCopy}
    </div>
  );
}
